// ============================================
// COMPONENT: PlantDetailView
// ============================================
// Archivo: src/components/plant/PlantDetailView.jsx

import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  ChevronDown, 
  ChevronRight,
  Activity,
  LogOut,
  AlertTriangle,
  UserCog
} from 'lucide-react';
import FaultsSection from './FaultsSection';

const PlantDetailView = ({ 
  plant, 
  equipments,
  faults = [],
  user,
  onBack,
  onLogout,
  onOpenMyData,
  onCreateEquipment,
  onEditEquipment,
  onDeleteEquipment,
  onViewEquipment,
  onRegisterFault,
  onUpdateFault,
  getCalibrationStatus
}) => {
  const [expandedLines, setExpandedLines] = useState({});

  // Equipos de esta planta
  const plantEquipments = useMemo(() => 
    equipments.filter(e => e.plantId === plant.id),
    [equipments, plant.id]
  );

  // Agrupar equipos por línea
  const equipmentsByLine = useMemo(() => {
    const grouped = {};
    
    // Crear todas las líneas de la planta
    for (let i = 1; i <= (plant.lines || 4); i++) {
      grouped[`L${i}`] = [];
    }

    // Agrupar equipos
    plantEquipments.forEach(equip => {
      const line = equip.line || 'L1';
      if (!grouped[line]) {
        grouped[line] = [];
      }
      grouped[line].push(equip);
    });

    return grouped;
  }, [plantEquipments, plant.lines]);

  // Verificar si hay falla con impacto general activa en esta planta
  const hasGeneralImpactFault = useMemo(() => 
    faults.some(f => f.equipo_afectado === 'General Planta' && f.estado !== 'resuelta' && f.estado !== 'cerrada'),
    [faults]
  );

  const toggleLine = (line) => {
    setExpandedLines(prev => ({
      ...prev,
      [line]: !prev[line]
    }));
  };

  const canEdit = user.role === 'Administrador' || user.role === 'Gerente';
  const canDelete = user.role === 'Administrador';
  
  // Permisos para fallas: Todos menos Operador
  const canReportFault = user.role !== 'Operador';
  const canUpdateFault = user.role !== 'Operador';

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Navigation */}
      <nav className="bg-slate-800 px-4 py-4 border-b border-slate-700">
        <div className="flex flex-wrap justify-between items-center gap-y-2">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              className="text-cyan-400 hover:text-cyan-300"
            >
              <ArrowLeft size={24} />
            </button>
            <Activity size={28} className="text-cyan-400 hidden sm:block" />
            <div className="text-white">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg sm:text-xl font-bold">{plant.name}</h1>
                {canReportFault && onRegisterFault && (
                  <button
                    onClick={() => onRegisterFault(null)}
                    className="px-2 py-1 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1 text-xs font-semibold"
                    title="Registrar falla general de planta"
                  >
                    <AlertTriangle size={12} />
                    <span className="hidden sm:inline">Falla General</span>
                  </button>
                )}
              </div>
              <p className="text-xs text-slate-400">
                {plant.code} - {plant.location}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <p className="text-sm text-cyan-400 hidden sm:block">
              {new Date().toLocaleString('es-CL')}
            </p>
            {onOpenMyData && (
              <button onClick={onOpenMyData} title="Mis Datos" className="p-2 sm:px-3 sm:py-2 text-slate-300 hover:text-white flex items-center gap-1 text-sm">
                <UserCog size={15} /><span className="hidden sm:inline">Mis Datos</span>
              </button>
            )}
            <button
              onClick={onLogout}
              className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1 text-sm"
            >
              <LogOut size={16} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="p-6">
        {/* Plant Info Card */}
        <div className="bg-slate-800 rounded-lg p-6 border border-slate-700 mb-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <p className="text-slate-400 text-sm">Jefe de Planta</p>
              <p className="text-white font-semibold">{plant.manager || 'No asignado'}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Horario</p>
              <p className="text-white font-semibold">{plant.schedule || '08:00-20:00'}</p>
            </div>
            <div>
              <p className="text-slate-400 text-sm">Contacto Emergencia</p>
              <p className="text-white font-semibold">{plant.emergency || 'No disponible'}</p>
            </div>
          </div>
        </div>


        {/* Fallas Activas */}
        {faults && faults.length > 0 && onUpdateFault && canUpdateFault && (
          <FaultsSection 
            faults={faults}
            onUpdateFault={onUpdateFault}
            canUpdate={canUpdateFault}
          />
        )}

        {/* Lines */}
        <div className="space-y-4">
          {Object.keys(equipmentsByLine).sort().map(line => {
            const lineEquipments = equipmentsByLine[line];
            const isExpanded = expandedLines[line];
            const operativeCount = lineEquipments.filter(e => e.status === 'operativo').length;
            const totalCount = lineEquipments.length;
            
            // Estado de la línea: si hay falla general, TODA la planta está en rojo
            // Si no, verificar fallas específicas de la línea o calibraciones vencidas
            const hasFailure = hasGeneralImpactFault ||
              lineEquipments.some(e => e.status === 'falla' || e.status === 'en_falla') ||
              lineEquipments.some(e => getCalibrationStatus && getCalibrationStatus(e.id) === 'expired');

            return (
              <div key={line} className="bg-slate-800 rounded-lg border border-slate-700">
                {/* Line Header */}
                <div
                  onClick={() => toggleLine(line)}
                  className="p-4 flex justify-between items-center cursor-pointer hover:bg-slate-750"
                >
                  <div className="flex items-center gap-4">
                    {isExpanded ? (
                      <ChevronDown className="text-cyan-400" size={20} />
                    ) : (
                      <ChevronRight className="text-cyan-400" size={20} />
                    )}
                    <h3 className="text-xl font-bold text-white">
                      Línea {line}
                    </h3>
                    <div className="flex items-center gap-2">
                      <div 
                        className={`w-3 h-3 rounded-full ${
                          hasFailure ? 'bg-red-500 animate-pulse' : 'bg-green-500'
                        }`}
                      />
                      <span className="text-slate-300 text-sm font-semibold">
                        {operativeCount}/{totalCount} operativos
                      </span>
                    </div>
                  </div>

                  {canEdit && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onCreateEquipment(plant.id, line);
                      }}
                      className="px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center gap-2"
                    >
                      <Plus size={16} />
                      Agregar Equipo
                    </button>
                  )}
                </div>

                {/* Line Content */}
                {isExpanded && (
                  <div className="p-4 border-t border-slate-700">
                    {lineEquipments.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-slate-400">No hay equipos en esta línea</p>
                        {canEdit && (
                          <button
                            onClick={() => onCreateEquipment(plant.id, line)}
                            className="mt-4 px-6 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700"
                          >
                            Agregar Primer Equipo
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {lineEquipments.map(equip => (
                          <EquipmentCard
                            key={equip.id}
                            equipment={equip}
                            canEdit={canEdit}
                            canDelete={canDelete}
                            canReportFault={canReportFault}
                            onView={() => onViewEquipment(equip)}
                            onEdit={() => onEditEquipment(equip)}
                            onDelete={() => onDeleteEquipment(equip.id)}
                            onCreateFault={() => onRegisterFault(equip)}
                            calibrationStatus={getCalibrationStatus ? getCalibrationStatus(equip.id) : null}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================
// SUB-COMPONENT: EquipmentCard
// ============================================
const EquipmentCard = ({
  equipment,
  canEdit,
  canDelete,
  canReportFault,
  onView,
  onEdit,
  onDelete,
  onCreateFault,
  calibrationStatus
}) => {
  const statusColors = {
    operativo: 'bg-green-500',
    falla: 'bg-red-500',
    en_falla: 'bg-red-500',  // Alias para falla
    mantenimiento: 'bg-yellow-500',
    deshabilitado: 'bg-gray-500'
  };

  const statusText = {
    operativo: 'Operativo',
    falla: 'EN FALLA',
    en_falla: 'EN FALLA',  // Alias para falla
    mantenimiento: 'En Mantenimiento',
    deshabilitado: 'Deshabilitado'
  };

  const calibrationColors = {
    ok: 'text-green-400',
    warning: 'text-yellow-400',
    expired: 'text-red-400',
    'no-data': 'text-slate-400'
  };

  const calibrationText = {
    ok: 'Vigente',
    warning: 'Por vencer',
    expired: 'Vencida',
    'no-data': 'Sin datos'
  };

  return (
    <div className={`bg-slate-750 rounded-lg p-4 border transition-all ${
      (equipment.status === 'falla' || equipment.status === 'en_falla')
        ? 'border-red-500 bg-red-900/10 hover:border-red-400'
        : (equipment.status === 'deshabilitado' || equipment.status === 'mantenimiento')
        ? 'border-yellow-500 bg-yellow-900/10 hover:border-yellow-400'
        : equipment.status === 'operativo'
        ? 'border-cyan-600 hover:border-cyan-400'
        : 'border-slate-600'
    }`}>
      {/* Status Indicator */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${statusColors[equipment.status] || 'bg-gray-500'}`} />
          <span className="text-xs text-slate-400">
            {statusText[equipment.status] || equipment.status}
          </span>
        </div>
        {calibrationStatus && (
          <span className={`text-xs ${calibrationColors[calibrationStatus]}`}>
            📋 {calibrationText[calibrationStatus]}
          </span>
        )}
      </div>

      {/* Equipment Info */}
      <h4 className="text-white font-bold mb-2">{equipment.name}</h4>
      
      <div className="space-y-1 text-sm">
        {equipment.manufacturer && (
          <p className="text-slate-300">
            <span className="text-slate-500">Marca:</span> {equipment.manufacturer}
          </p>
        )}
        
        {equipment.model && (
          <p className="text-slate-300">
            <span className="text-slate-500">Modelo:</span> {equipment.model}
          </p>
        )}
        
        {equipment.equipment_code && (
          <p className="text-slate-300">
            <span className="text-slate-500">Código:</span> {equipment.equipment_code}
          </p>
        )}
        
        {equipment.serial && (
          <p className="text-slate-400 text-xs mt-2">
            Serie: {equipment.serial}
          </p>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 mt-3 pt-3 border-t border-slate-600">
        <button
          onClick={onView}
          className="flex-1 px-3 py-2 bg-cyan-600 text-white rounded hover:bg-cyan-700 text-sm"
        >
          Ver
        </button>
        {canReportFault && onCreateFault && (
          <button
            onClick={onCreateFault}
            className="flex-1 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm flex items-center justify-center gap-1"
          >
            <AlertTriangle size={14} />
            Falla
          </button>
        )}
        {canEdit && (
          <button
            onClick={onEdit}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            <Edit size={16} />
          </button>
        )}
        {canDelete && (
          <button
            onClick={onDelete}
            className="px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            <Trash2 size={16} />
          </button>
        )}
      </div>
    </div>
  );
};

export default PlantDetailView;